/**
 * Promote-only canonical SRP URL: the path with its slug segment swapped to the
 * BE's `resolvedSlug`, keeping query + page + locale verbatim — or `null` when
 * already on the resolved slug.
 *
 * No param stripping: redundant params (e.g. `?pets=cats-dogs` on
 * `/pet-friendly-…`) are kept, like legacy. That keeps the client morph a single
 * navigation and means the server never issues a strip-redirect mid-nav (which
 * is what blanked the page). Pure, so the server route (load-time redirect) and
 * the client hook (`useSrpSlugCanonicalization`) share it.
 */
export function computeCanonicalSrpUrl(args: {
  pathname: string;
  search: string;
  locales: ReadonlyArray<string>;
  resolvedSlug: string | undefined;
}): string | null {
  const { pathname, search, locales, resolvedSlug } = args;
  if (!resolvedSlug) return null;

  const segments = pathname.split('/').filter(Boolean);
  const hasLocale = (locales as ReadonlyArray<string>).includes(segments[0] ?? '');
  const localePrefix = hasLocale ? `/${segments[0]}` : '';
  const route = hasLocale ? segments.slice(1) : segments;
  const [resourceId, currentSlug, maybePage, pageNum] = route;
  if (!resourceId || !currentSlug) return null;
  if (currentSlug === resolvedSlug) return null; // already canonical

  const pageTail = maybePage === 'page' && pageNum ? `/page/${pageNum}` : '';
  const normalizedSearch = search && !search.startsWith('?') ? `?${search}` : search;
  return `${localePrefix}/${resourceId}/${resolvedSlug}${pageTail}${normalizedSearch}`;
}
